import { useQuery } from '@tanstack/react-query';

import {
  type CreditAllowanceSeatType,
  GET_WORKSPACE_MEMBER_CREDIT_ALLOWANCES,
  type WorkspaceMemberCreditAllowance,
} from '../../contracts';
import {
  getWorkspaceMemberCreditAllowances,
  WORKSPACE_MEMBER_CREDIT_ALLOWANCES_PAGE_SIZE,
} from '../api/credit-allowance';
import { getCommerceDataConfig } from '../config';
import { toWorkspaceTeamUsage, type WorkspaceTeamUsage } from './credit-allowance.utils';
import { CREDIT_ALLOWANCE_STALE_TIME_MS } from './useQueryCreditAllowance';

/** `user_ref_id` → the seat of the member's current cycle; `null` when the cycle sells no seat. */
export type WorkspaceMemberSeats = ReadonlyMap<string, CreditAllowanceSeatType | null>;

type HttpErrorShape = { response?: { status?: number } };

const errorStatus = (error: unknown): number | undefined =>
  (error as HttpErrorShape | null)?.response?.status;

/** Walks every page of the admin-only member allowance list. */
export const fetchAllWorkspaceMemberCreditAllowances = async (
  workspaceId: string
): Promise<WorkspaceMemberCreditAllowance[]> => {
  const members: WorkspaceMemberCreditAllowance[] = [];
  let page = 1;
  let total = Number.POSITIVE_INFINITY;
  while (members.length < total) {
    const { data } = await getWorkspaceMemberCreditAllowances(workspaceId, { page });
    members.push(...data.members);
    total = data.total;
    // A short page means the list ended regardless of what `total` claims.
    if (data.members.length < WORKSPACE_MEMBER_CREDIT_ALLOWANCES_PAGE_SIZE) break;
    page += 1;
  }
  return members;
};

export const toWorkspaceMemberSeats = (
  members: readonly WorkspaceMemberCreditAllowance[]
): WorkspaceMemberSeats =>
  new Map(
    members.map((member) => [member.user_ref_id, member.allowance?.seat_type ?? null])
  );

export type UseQueryWorkspaceMemberSeatsProps = {
  workspaceId: string | null | undefined;
  enabled?: boolean;
};

/**
 * One cached member allowance list per workspace, shared by every reader that
 * projects it (seats, team usage). Workspace admins only — a 403 resolves to an
 * empty list rather than an error, since a non-admin viewer has nothing to show.
 */
const useQueryWorkspaceMemberCreditAllowances = <TData>({
  workspaceId,
  enabled = true,
  select,
}: UseQueryWorkspaceMemberSeatsProps & {
  select: (members: WorkspaceMemberCreditAllowance[]) => TData;
}) => {
  const { useAuthToken } = getCommerceDataConfig();
  const token = useAuthToken();

  return useQuery({
    queryKey: [GET_WORKSPACE_MEMBER_CREDIT_ALLOWANCES, workspaceId],
    enabled: enabled && !!token && !!workspaceId,
    staleTime: CREDIT_ALLOWANCE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      const status = errorStatus(error);
      if (status !== undefined && status < 500) return false;
      return failureCount < 2;
    },
    queryFn: async (): Promise<WorkspaceMemberCreditAllowance[]> => {
      try {
        return await fetchAllWorkspaceMemberCreditAllowances(workspaceId as string);
      } catch (error: unknown) {
        if (errorStatus(error) === 403) return [];
        throw error;
      }
    },
    select,
  });
};

/** Every workspace member's current seat, for the Members table. */
export function useQueryWorkspaceMemberSeats(props: UseQueryWorkspaceMemberSeatsProps) {
  return useQueryWorkspaceMemberCreditAllowances({
    ...props,
    select: toWorkspaceMemberSeats,
  });
}

export type UseQueryWorkspaceTeamUsageProps = UseQueryWorkspaceMemberSeatsProps;

/** The workspace's aggregate credit usage this cycle, summed over every member. */
export function useQueryWorkspaceTeamUsage(props: UseQueryWorkspaceTeamUsageProps) {
  return useQueryWorkspaceMemberCreditAllowances<WorkspaceTeamUsage>({
    ...props,
    select: toWorkspaceTeamUsage,
  });
}
